import type {
  DocumentTypeDescriptor,
  FieldPredicateExpression,
  FilterExpression,
  QueryDiagnostic,
  QueryFieldDescriptor,
  QueryJsonPrimitive,
  QueryOperatorId,
  QuerySessionState,
  TextSearchExpression,
} from '@aeppic/query/contracts'
import type { Query } from '@aeppic/query'

import { Scopes } from '../../model/query/index.js'

export interface CompileExpressionsResult {
  query?: Query
  diagnostics: QueryDiagnostic[]
}

export function compileSessionExpressions(session: QuerySessionState, descriptors: readonly DocumentTypeDescriptor[]): CompileExpressionsResult {
  const fields = new Map(descriptors.flatMap(descriptor => descriptor.fields).map(field => [field.path, field]))
  const diagnostics: QueryDiagnostic[] = []
  const contributions: Array<{ query: Query, sameBranch: boolean }> = []
  const expressions: Array<{ expression: FilterExpression | null, path: readonly (string | number)[] }> = [
    { expression: session.search, path: ['search'] },
    { expression: session.toolbarFilter, path: ['toolbarFilter'] },
    ...Object.keys(session.contributions).sort().map(key => ({ expression: session.contributions[key], path: ['contributions', key] })),
  ]

  for (const { expression, path } of expressions) {
    if (!expression) {
      continue
    }

    const compiled = compileExpression(expression, fields, path)
    diagnostics.push(...compiled.diagnostics)
    if (compiled.query && !isEmptyQuery(compiled.query)) {
      contributions.push({ query: compiled.query, sameBranch: expression.kind === 'field' })
    }
  }

  if (diagnostics.some(diagnostic => diagnostic.severity === 'error')) {
    return { diagnostics }
  }

  const builder = Scopes.filter(session.baseQuery.options ?? {})
  if (!isEmptyQuery(session.baseQuery)) {
    builder.and(asSameBranchQuery(session.baseQuery))
  }
  for (const { query, sameBranch } of contributions) {
    builder.and(sameBranch ? asSameBranchQuery(query) : query)
  }

  return { query: builder.toQuery(), diagnostics }
}

function compileExpression(expression: FilterExpression, fields: ReadonlyMap<string, QueryFieldDescriptor>, path: readonly (string | number)[]): CompileExpressionsResult {
  switch (expression.kind) {
    case 'group':
      return compileGroup(expression.children, expression.operator, fields, path)
    case 'field':
      return compileField(expression, fields, path)
    case 'text-search':
      return compileTextSearch(expression, fields, path)
    case 'relation':
      return unsupported('unsupported-relation-filter', 'Relation filters are implemented in Phase 6', path)
    case 'named-fragment':
      return unsupported('unsupported-named-fragment', 'Named fragments are implemented in Phase 7', path)
  }
}

function compileGroup(children: readonly FilterExpression[], operator: 'AND' | 'OR', fields: ReadonlyMap<string, QueryFieldDescriptor>, path: readonly (string | number)[]): CompileExpressionsResult {
  const diagnostics: QueryDiagnostic[] = []
  const queries: Query[] = []

  children.forEach((child, index) => {
    const compiled = compileExpression(child, fields, [...path, 'children', index])
    diagnostics.push(...compiled.diagnostics)
    if (compiled.query && !isEmptyQuery(compiled.query)) {
      queries.push(compiled.query)
    }
  })

  if (diagnostics.some(diagnostic => diagnostic.severity === 'error') || queries.length === 0) {
    return { diagnostics }
  }

  const builder = Scopes.filter()
  queries.forEach((query, index) => {
    if (operator === 'OR' && index > 0) {
      builder.or.and(query)
    } else {
      builder.and(query)
    }
  })

  return { query: builder.toQuery(), diagnostics }
}

function compileField(expression: FieldPredicateExpression, fields: ReadonlyMap<string, QueryFieldDescriptor>, path: readonly (string | number)[]): CompileExpressionsResult {
  const descriptor = fields.get(expression.field)
  if (!descriptor) {
    return unsupported('unknown-field', `Unknown query field ${expression.field}`, [...path, 'field'])
  }

  if (!descriptor.operators.includes(expression.operator)) {
    return unsupported('unsupported-operator', `Operator ${expression.operator} is not supported for ${expression.field}`, [...path, 'operator'])
  }

  const values = readPrimitiveValues(expression.value)
  if (!values && expression.operator !== 'empty' && expression.operator !== 'not_empty') {
    return unsupported('invalid-operator-value', `Operator ${expression.operator} requires a scalar value or scalar array`, [...path, 'value'])
  }

  return compileFieldOperation(descriptor, expression.operator, values ?? [])
}

function compileFieldOperation(descriptor: QueryFieldDescriptor, operator: QueryOperatorId, values: QueryJsonPrimitive[]): CompileExpressionsResult {
  const field = descriptor.path
  const value = values[0]
  const builder = Scopes.filter()

  if (descriptor.valueKinds.includes('boolean') && typeof descriptor.missingValue === 'boolean' && typeof value === 'boolean' && (operator === 'is' || operator === 'is_not')) {
    const expectedValue = operator === 'is' ? value : !value
    const query = expectedValue === descriptor.missingValue
      ? builder.where(field).isNot(!expectedValue).toQuery()
      : builder.where(field).is(expectedValue).toQuery()
    return { query, diagnostics: [] }
  }

  switch (operator) {
    case 'contains':
      return stringValue(value, operator, value => builder.where(field).contains(value).toQuery())
    case 'not_contains':
      return stringValue(value, operator, value => negateQuery(builder.where(field).contains(value).toQuery()))
    case 'starts_with':
      return stringValue(value, operator, value => builder.where(field).startsWith(value).toQuery())
    case 'equals':
      return primitiveValue(value, operator, value => builder.where(field).isStrict(value).toQuery())
    case 'not_equals':
      return primitiveValue(value, operator, value => negateQuery(builder.where(field).isStrict(value).toQuery()))
    case 'eq':
    case 'is':
      return primitiveValue(value, operator, value => builder.where(field).is(value).toQuery())
    case 'neq':
    case 'is_not':
      return primitiveValue(value, operator, value => builder.where(field).isNot(value).toQuery())
    case 'is_one_of':
      return { query: builder.where(field).isOneOf(nonNullPrimitives(values)).toQuery(), diagnostics: [] }
    case 'is_not_one_of':
      return combineNegativeValues(field, nonNullPrimitives(values))
    case 'not_empty':
      return { query: builder.where(field).isTruthy().toQuery(), diagnostics: [] }
    case 'empty':
      return { query: negateQuery(builder.where(field).isTruthy().toQuery()), diagnostics: [] }
    case 'gte':
      return rangeValue(value, operator, value => builder.where(field).isInRange(value, '*').toQuery())
    case 'lte':
      return rangeValue(value, operator, value => builder.where(field).isInRange('*', value).toQuery())
    case 'gt':
    case 'after':
      return rangeValue(value, operator, value => setRangeInclusive(builder.where(field).isInRange(value, '*').toQuery(), false))
    case 'lt':
    case 'before':
      return rangeValue(value, operator, value => setRangeInclusive(builder.where(field).isInRange('*', value).toQuery(), false))
    case 'between':
      if (values.length !== 2 || values.some(value => value === null)) {
        return unsupported('invalid-operator-value', 'Operator between requires exactly two non-null values', ['value'])
      }
      return { query: builder.where(field).isInRange(values[0] as string | number, values[1] as string | number).toQuery(), diagnostics: [] }
    default:
      return unsupported('unsupported-operator', `Operator ${operator} is not implemented`, ['operator'])
  }
}

function compileTextSearch(expression: TextSearchExpression, fields: ReadonlyMap<string, QueryFieldDescriptor>, path: readonly (string | number)[]): CompileExpressionsResult {
  const hasExplicitFields = Boolean(expression.fields?.length)
  const requestedFields = hasExplicitFields ? [...expression.fields] : ['data.name', 'data.label']
  const searchFields = hasExplicitFields ? requestedFields : requestedFields.filter(field => fields.has(field))
  const diagnostics = (hasExplicitFields ? searchFields : [])
    .filter(field => !fields.has(field))
    .map(field => diagnostic('unknown-field', `Unknown text-search field ${field}`, [...path, 'fields']))

  if (diagnostics.length > 0) {
    return { diagnostics }
  }

  if (searchFields.length === 0) {
    return unsupported('missing-text-search-field', 'The document type has no default text-search field', [...path, 'fields'])
  }

  const terms = tokenizeSearch(expression)
  const termQueries = terms.map(term => {
    const fieldQueries = searchFields.map(field => {
      if (expression.mode === 'contains') {
        return Scopes.filter().where(field).contains(term).toQuery()
      }
      if (expression.leadingWildcard || expression.trailingWildcard || expression.mode === 'phrase') {
        return compileBoundaryText(field, term, Boolean(expression.leadingWildcard), Boolean(expression.trailingWildcard))
      }
      if (expression.mode === 'prefix') {
        return combineQueries([
          Scopes.filter().where(field).startsWith(term).toQuery(),
          Scopes.filter().where(field).contains(` ${term}`).toQuery(),
        ], 'OR')
      }
      return Scopes.filter().where(field).contains(term).toQuery()
    })
    return combineQueries(fieldQueries, 'OR')
  })

  return { query: combineQueries(termQueries, expression.combine ?? 'AND'), diagnostics: [] }
}

function compileBoundaryText(field: string, term: string, leadingWildcard: boolean, trailingWildcard: boolean): Query {
  if (leadingWildcard && trailingWildcard) {
    return Scopes.filter().where(field).contains(term).toQuery()
  }
  if (leadingWildcard) {
    return combineQueries([
      Scopes.filter().where(field).endsWith(term).toQuery(),
      Scopes.filter().where(field).contains(`${term} `).toQuery(),
    ], 'OR')
  }
  if (trailingWildcard) {
    return combineQueries([
      Scopes.filter().where(field).startsWith(term).toQuery(),
      Scopes.filter().where(field).contains(` ${term}`).toQuery(),
    ], 'OR')
  }

  return combineQueries([
    caseInsensitiveLiteral(field, term),
    Scopes.filter().where(field).startsWith(`${term} `).toQuery(),
    Scopes.filter().where(field).endsWith(` ${term}`).toQuery(),
    Scopes.filter().where(field).contains(` ${term} `).toQuery(),
  ], 'OR')
}

function caseInsensitiveLiteral(field: string, value: string): Query {
  const query = Scopes.filter().where(field).is(value).toQuery()
  return {
    ...query,
    graph: {
      ...query.graph,
      conditions: query.graph.conditions.map(condition => 'field' in condition
        ? { ...condition, termQuotes: 'single' }
        : condition),
    },
  }
}

function tokenizeSearch(expression: TextSearchExpression): string[] {
  const text = expression.text.trim()
  if (!text) {
    return []
  }
  if (expression.mode === 'phrase') {
    return [text.replace(/^"|"$/g, '')]
  }

  const terms: string[] = []
  const matcher = /"([^"]+)"|(\S+)/g
  let match: RegExpExecArray
  while ((match = matcher.exec(text)) !== null) {
    terms.push(match[1] ?? match[2])
  }
  return terms
}

function combineQueries(queries: readonly Query[], operator: 'AND' | 'OR'): Query {
  const builder = Scopes.filter()
  queries.filter(query => !isEmptyQuery(query)).forEach((query, index) => {
    if (operator === 'OR' && index > 0) {
      builder.or.and(query)
    } else {
      builder.and(query)
    }
  })
  return builder.toQuery()
}

function combineNegativeValues(field: string, values: Array<string | number | boolean>): CompileExpressionsResult {
  const queries = values.map(value => Scopes.filter().where(field).isNot(value).toQuery())
  return { query: combineQueries(queries, 'AND'), diagnostics: [] }
}

function negateQuery(query: Query): Query {
  return {
    ...query,
    graph: {
      ...query.graph,
      conditions: query.graph.conditions.map(condition => 'field' in condition
        ? { ...condition, termPrefix: condition.termPrefix === '-' ? undefined : '-' }
        : condition),
    },
  }
}

function setRangeInclusive(query: Query, inclusive: boolean): Query {
  return {
    ...query,
    graph: {
      ...query.graph,
      conditions: query.graph.conditions.map(condition => 'field' in condition ? { ...condition, inclusive } : condition),
    },
  }
}

function readPrimitiveValues(value: unknown): QueryJsonPrimitive[] | null {
  const values = Array.isArray(value) ? value : [value]
  return values.every(isPrimitive) ? values : null
}

function nonNullPrimitives(values: QueryJsonPrimitive[]): Array<string | number | boolean> {
  return values.filter((value): value is string | number | boolean => value !== null)
}

function isPrimitive(value: unknown): value is QueryJsonPrimitive {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

function primitiveValue(value: QueryJsonPrimitive, operator: QueryOperatorId, compile: (value: string | number | boolean) => Query): CompileExpressionsResult {
  return value === null
    ? unsupported('invalid-operator-value', `Operator ${operator} does not accept null`, ['value'])
    : { query: compile(value), diagnostics: [] }
}

function stringValue(value: QueryJsonPrimitive, operator: QueryOperatorId, compile: (value: string) => Query): CompileExpressionsResult {
  return typeof value === 'string'
    ? { query: compile(value), diagnostics: [] }
    : unsupported('invalid-operator-value', `Operator ${operator} requires a string`, ['value'])
}

function rangeValue(value: QueryJsonPrimitive, operator: QueryOperatorId, compile: (value: string | number) => Query): CompileExpressionsResult {
  return typeof value === 'string' || typeof value === 'number'
    ? { query: compile(value), diagnostics: [] }
    : unsupported('invalid-operator-value', `Operator ${operator} requires a string or number`, ['value'])
}

function isEmptyQuery(query: Query): boolean {
  return query.graph.conditions.length === 0
}

function asSameBranchQuery(query: Query): Query {
  if ('operator' in query.graph) {
    return query
  }

  return {
    ...query,
    graph: {
      ...query.graph,
      operator: 'AND',
    },
  }
}

function unsupported(code: string, message: string, path: readonly (string | number)[]): CompileExpressionsResult {
  return { diagnostics: [diagnostic(code, message, path)] }
}

function diagnostic(code: string, message: string, path: readonly (string | number)[]): QueryDiagnostic {
  return { code, message, severity: 'error', path }
}
